import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchAll } from "@/lib/content";
import { news } from "@/mocks/news";
import { videos } from "@/mocks/videos";
import VideoCard from "@/components/base/VideoCard";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const [input, setInput] = useState(initial);
  const [keyword, setKeyword] = useState(initial);
  const [kind, setKind] = useState<"all" | "news" | "video">("all");

  usePageTitle("搜索 - 91.con娱乐资讯与视频", "在91.con搜索娱乐资讯、影视综艺视频与热点专题内容。");

  const results = useMemo(() => searchAll(keyword), [keyword]);
  const filtered = kind === "all" ? results : results.filter((r) => r.kind === kind);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setKeyword(input.trim());
    setParams(input.trim() ? { q: input.trim() } : {});
  };

  const recommendations = [
    ...news.slice(0, 2).map((n) => ({ kind: "news" as const, item: n })),
    ...videos.slice(0, 1).map((v) => ({ kind: "video" as const, item: v })),
  ];

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索</span>
      </nav>

      <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">搜索内容</h1>
        <p className="mt-2 text-sm text-foreground-600">
          支持资讯与视频双向搜索，基于标题与关键词匹配（前端模拟演示）。
        </p>
        <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500"></i>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入关键词，如：科幻、综艺、周深..."
              className="w-full rounded-md border border-background-200 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>
      </div>

      {keyword ? (
        <div className="mt-8">
          <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-700">
              关键词「<span className="text-primary-400">{keyword}</span>」共找到{" "}
              <span className="font-bold text-foreground-950">{filtered.length}</span> 条结果
            </p>
            <div className="flex items-center gap-1 rounded-full border border-background-200 px-1 py-1">
              {[
                { key: "all", label: "全部" },
                { key: "news", label: "资讯" },
                { key: "video", label: "视频" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setKind(t.key as "all" | "news" | "video")}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    kind === t.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="flex flex-col gap-4">
              {filtered.map((r) => (
                <Link
                  key={`${r.kind}-${r.id}`}
                  to={r.kind === "news" ? `/news/${r.id}` : `/video/${r.id}`}
                  className="group flex flex-col gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-400/60 sm:flex-row"
                >
                  <div className="h-40 w-full flex-shrink-0 overflow-hidden rounded-md sm:h-24 sm:w-40">
                    <img
                      src={r.cover}
                      alt={`${r.title} - 搜索结果`}
                      title={`${r.title} - 91.con`}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-sm px-2 py-0.5 text-xs font-medium ${
                          r.kind === "news"
                            ? "bg-primary-500/15 text-primary-300"
                            : "bg-accent-500/20 text-accent-300"
                        }`}
                      >
                        {r.kind === "news" ? "资讯" : "视频"}
                      </span>
                      <span className="text-xs text-foreground-500">{r.meta}</span>
                    </div>
                    <h3 className="line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-400 md:text-lg">
                      {r.title}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-sm text-foreground-600">{r.summary}</p>
                    <span className="mt-2 flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-calendar-line"></i>
                      {r.date}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-100 py-16">
              <i className="ri-search-eye-line text-4xl text-foreground-400"></i>
              <p className="text-sm text-foreground-600">
                没有找到与「{keyword}」相关的内容，换个关键词试试吧。
              </p>
            </div>
          )}

          {/* 无结果 / 底部推荐 */}
          <div className="mt-12">
            <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-accent-500"></span>为你推荐
            </h2>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              {recommendations.map((r) =>
                r.kind === "video" ? (
                  <VideoCard key={`rec-${r.item.id}`} item={r.item} />
                ) : (
                  <Link
                    key={`rec-${r.item.id}`}
                    to={`/news/${r.item.id}`}
                    className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-400/60"
                  >
                    <div className="h-44 w-full overflow-hidden">
                      <img
                        src={r.item.cover}
                        alt={`${r.item.title} - 推荐内容`}
                        title={`${r.item.title} - 91.con`}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-400">
                        {r.item.title}
                      </h3>
                      <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{r.item.summary}</p>
                    </div>
                  </Link>
                ),
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>热门推荐
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {videos.slice(0, 3).map((v) => (
              <VideoCard key={v.id} item={v} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}